import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { forkJoin, map, Observable } from 'rxjs';

import { IFlagDefinition, IGameEvent, IGameEventDefinition, IGameEventsRequestParams, IPlayerEvent, IPlayerEventDefinition, IPlayerEventsRequestParams, IShiftEventDefinition, IPlayerShiftEventsRequestParams, IPlayerEventShiftsApiResponse, IPlayerEventPayload, IGameEventPayload, ICreateEventApiResponse } from './event.models';

import { convertObjectToRestapiParams } from 'src/app/utils/api.utils';

@Injectable()
export class EventService {
    private _http = inject(HttpClient);

    public getGameEventDefinitionsApi(): Observable<IGameEventDefinition[]> {
        return this._http.get<IGameEventDefinition[]>(`gameeventdefinitions`);
    }

    public getPlayerEventDefinitionsApi(): Observable<IPlayerEventDefinition[]> {
        return this._http.get<IPlayerEventDefinition[]>(`playereventdefinitions`);
    }

    public getShiftEventDefinitionsAPI(): Observable<IShiftEventDefinition[]> {
        return this._http.get<IShiftEventDefinition[]>(`/definitions/playershiftevent`);
    }

    public getFlagDefinitionsApi(): Observable<IFlagDefinition[]> {
        return this._http.get<IFlagDefinition[]>(`flagdefinitions`);
    }

    public getGameEventsApi(params: IGameEventsRequestParams): Observable<IGameEvent[]> {
        return this._http.get<IGameEvent[]>(`gameevents`, {
            params: convertObjectToRestapiParams(params)
        });
    }

    public getPlayerEventsApi(params: IPlayerEventsRequestParams): Observable<IPlayerEvent[]> {
        return this._http.get<IPlayerEvent[]>(`playerevents`, {
            params: convertObjectToRestapiParams(params)
        });
    }

    public getPlayerShiftEventsApi(params: IPlayerShiftEventsRequestParams): Observable<IPlayerEventShiftsApiResponse[]> {
        return this._http.get<IPlayerEventShiftsApiResponse[]>(`playershiftevents`, {
            params: convertObjectToRestapiParams(params)
        });
    }

    public deleteGameEventsApi(eventIds: string[]): Observable<void> {
        return this._http.delete<void>(`gameevents`, {
            params: convertObjectToRestapiParams({ gameeventid: eventIds })
        });
    }

    public deletePlayerEventsApi(eventIds: string[]): Observable<void> {
        return this._http.delete<void>(`playerevents`, {
            params: convertObjectToRestapiParams({ playereventid: eventIds })
        });
    }

    public deletePlayerShiftEventsApi(eventIds: string[]): Observable<void> {
        return this._http.delete<void>(`playershiftevents`, {
            params: convertObjectToRestapiParams({ playershifteventid: eventIds })
        });
    }

    /**
     * Deletes a mixed selection as one operation. Each event type still uses its own
     * endpoint; the observable errors unless every request succeeds.
     */
    public deleteEventsApi(
        gameEventIds: readonly string[],
        playerEventIds: readonly string[],
        shiftEventIds: readonly string[]
    ): Observable<void> {
        const requests: Observable<void>[] = [];

        if (gameEventIds.length > 0) {
            requests.push(this.deleteGameEventsApi([...gameEventIds]));
        }
        if (playerEventIds.length > 0) {
            requests.push(this.deletePlayerEventsApi([...playerEventIds]));
        }
        if (shiftEventIds.length > 0) {
            requests.push(this.deletePlayerShiftEventsApi([...shiftEventIds]));
        }

        return forkJoin(requests).pipe(map(() => undefined));
    }

    public postPlayerEvent(gameId: string, payload: IPlayerEventPayload[]): Observable<ICreateEventApiResponse> {
        return this._http.post<ICreateEventApiResponse>(`games/${gameId}/events/player`, payload);
    }

    public postGameEvent(gameId: string, payload: IGameEventPayload): Observable<ICreateEventApiResponse> {
        return this._http.post<ICreateEventApiResponse>(`games/${gameId}/events/game`, payload);
    }
}
